<template>
  <!-- Drug<->diagnosis FDA-approval verdicts, one row per medication on the order.
       Reads order.drugIndicationChecks, prefetched by the order-detail serializer (no extra API call). -->
  <div v-if="checks.length" class="drug-indication mt-4">
    <div class="d-flex align-center ga-2 mb-2">
      <v-icon size="20" color="primary">mdi-pill</v-icon>
      <span class="text-subtitle-1 font-weight-bold">Drug–Diagnosis FDA Approval Check</span>
    </div>
    <div class="text-body-2 text-medium-emphasis mb-3">
      FDA-approval status of each medication against the order's diagnoses. Informational only — it does not change the
      submission. If a verdict looks wrong, you can request a correction for review.
    </div>

    <v-alert
      v-if="errorMsg"
      type="error"
      variant="tonal"
      density="compact"
      closable
      class="mb-3"
      @click:close="errorMsg = ''">
      {{ errorMsg }}
    </v-alert>

    <v-table density="comfortable" class="drug-indication__table">
      <thead>
        <tr>
          <th class="text-left">Medication</th>
          <th class="text-left">Diagnosis</th>
          <th class="text-left">FDA approval</th>
          <th class="text-left">Notes</th>
          <th class="text-left">Status/Action</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(check, index) in checks" :key="index">
          <td class="py-2">
            <div class="font-weight-medium">{{ check.medication || "—" }}</div>
            <div v-if="check.ingredientRxcui" class="text-caption text-medium-emphasis">
              RxCUI {{ check.ingredientRxcui }}
            </div>
          </td>
          <td class="py-2">{{ check.icd10Code || "—" }}</td>
          <td class="py-2">
            <div class="d-flex flex-column ga-1 align-start">
              <v-chip :color="verdictDisplay(check.verdict).color" size="small" variant="flat" label>
                <v-icon start size="small">{{ verdictDisplay(check.verdict).icon }}</v-icon>
                {{ verdictDisplay(check.verdict).label }}
              </v-chip>
              <v-chip v-if="check.doNotUse" color="error" size="x-small" variant="tonal" label>
                <v-icon start size="x-small">mdi-alert-octagon</v-icon>
                Contraindication / Boxed warning
              </v-chip>
            </div>
          </td>
          <td class="py-2 text-body-2">{{ check.reason || "—" }}</td>
          <td class="py-2">
            <div class="d-flex flex-column ga-1 align-start">
              <v-chip
                v-if="check.overrideStatus === OVERRIDE_STATUS.PENDING"
                color="info"
                variant="tonal"
                size="small"
                label>
                <v-icon start size="small">mdi-clock-outline</v-icon>
                Override pending review
              </v-chip>
              <v-chip
                v-else-if="check.overrideStatus === OVERRIDE_STATUS.APPROVED"
                color="success"
                variant="tonal"
                size="small"
                label>
                <v-icon start size="small">mdi-check</v-icon>
                Verdict set by Lamar review
              </v-chip>
              <v-chip
                v-else-if="check.overrideStatus === OVERRIDE_STATUS.DENIED"
                color="grey"
                variant="tonal"
                size="small"
                label>
                <v-icon start size="small">mdi-close</v-icon>
                Override denied
              </v-chip>
              <v-btn
                v-if="check.overrideStatus === OVERRIDE_STATUS.PENDING"
                size="small"
                variant="text"
                color="grey"
                text="Cancel request"
                :loading="withdrawingIndex === index"
                @click="withdrawRequest(index)" />
              <v-btn
                v-else-if="canRequestOverride(check)"
                size="small"
                variant="text"
                color="primary"
                text="Request change"
                @click="openDialog(index)" />
            </div>
          </td>
        </tr>
      </tbody>
    </v-table>

    <drug-indication-override-dialog
      :model-value="selectedCheckIndex !== -1"
      :order-key="orderKey"
      :check="selectedCheckIndex !== -1 ? checks[selectedCheckIndex] : null"
      @update:model-value="closeDialog"
      @submitted="onDialogSubmitted" />
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import DrugIndicationOverrideDialog from "./DrugIndicationOverrideDialog.vue";
import { orderInfoService } from "@/services/orderInfoService";
import { DISPUTABLE_VERDICTS, OVERRIDE_STATUS, verdictDisplay } from "@/constants/drugIndication";
import { tryParseApiErrors } from "@/utils";

const props = defineProps({
  order: {
    type: Object,
    required: true,
  },
});

const checks = computed(() => props.order.drugIndicationChecks || []);
const orderKey = computed(() => props.order.orderKey || "");

const selectedCheckIndex = ref(-1);
const withdrawingIndex = ref(-1);
const errorMsg = ref("");

function canRequestOverride(check) {
  // Can request override if:
  // 1. ingredientRxcui and icd10Code are non-empty
  // 2. verdict is disputable (on_label, off_label, not_indicated, or unverified)
  // 3. no UNDECIDED request exists — approved/denied rows may be disputed again
  return (
    check?.ingredientRxcui &&
    check?.icd10Code &&
    DISPUTABLE_VERDICTS.has(check?.verdict) &&
    check?.overrideStatus !== OVERRIDE_STATUS.PENDING
  );
}

function openDialog(index) {
  selectedCheckIndex.value = index;
}

function closeDialog(value) {
  if (!value) {
    selectedCheckIndex.value = -1;
  }
}

function onDialogSubmitted() {
  // Update the row's overrideStatus to pending locally
  if (selectedCheckIndex.value !== -1) {
    checks.value[selectedCheckIndex.value].overrideStatus = OVERRIDE_STATUS.PENDING;
  }
  selectedCheckIndex.value = -1;
}

async function withdrawRequest(index) {
  const check = checks.value[index];
  if (!check || withdrawingIndex.value !== -1) return;
  withdrawingIndex.value = index;
  errorMsg.value = "";
  try {
    await orderInfoService.withdrawDrugIndicationOverride(orderKey.value, check.medication);
    check.overrideStatus = "";
    check.overrideRequestedVerdict = "";
    check.overrideReason = "";
  } catch (e) {
    errorMsg.value = `Could not cancel the override request: ${tryParseApiErrors(e).join(" ")}`;
  } finally {
    withdrawingIndex.value = -1;
  }
}
</script>

<style scoped>
.drug-indication__table {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  overflow: hidden;
}
</style>
